import React, { useEffect, useRef, useState } from "react";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { toast } from "react-toastify";
import "../styles/bookingsReportMenu.css";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

/**
 * "Print Bookings" control for the Reservations tab row — pick a date range, then one of 4
 * delivery methods for that range's bookings (same columns as Table View).
 */
const BookingsReportMenu = () => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState("today");
  const [busyAction, setBusyAction] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePrint = async () => {
    setBusyAction("print");
    try {
      const res = await callAPI(apiUrls.RESERVATION_BOOKINGS_PRINT, {}, "POST", { range }, {});
      if (res.status === 1 || res.data?.status === 1) {
        toast.success(res.data?.message || "Sent to printer");
      } else {
        toast.error(res.data?.message || "Failed to print");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending to printer");
    } finally {
      setBusyAction(null);
    }
  };

  const handlePdf = async () => {
    setBusyAction("pdf");
    try {
      const res = await callAPI(
        apiUrls.RESERVATION_BOOKINGS_PDF,
        { range },
        "GET",
        null,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bookings-${range}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Error generating PDF");
    } finally {
      setBusyAction(null);
    }
  };

  const handleEmail = async () => {
    setBusyAction("email");
    try {
      const res = await callAPI(apiUrls.RESERVATION_BOOKINGS_EMAIL, {}, "POST", { range }, {});
      if (res.status === 1 || res.data?.status === 1) {
        toast.success(res.data?.message || "Emailed");
      } else {
        toast.error(res.data?.message || "Failed to email");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error sending email");
    } finally {
      setBusyAction(null);
    }
  };

  const handleLink = async () => {
    setBusyAction("link");
    try {
      const res = await callAPI(apiUrls.RESERVATION_BOOKINGS_LINK, {}, "POST", { range }, {});
      const url = res.data?.url;
      if (url) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Link copied to clipboard");
        } catch (clipboardErr) {
          toast.info(url);
        }
      } else {
        toast.error("Failed to create link");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating link");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="bookingsReportMenu" ref={wrapperRef}>
      <button
        type="button"
        className={`btn iconBtn bookingsReportTrigger${open ? " active" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>Print Bookings</span>
      </button>

      {open && (
        <div className="bookingsReportPanel">
          <div className="bookingsReportRanges">
            {RANGES.map((r) => (
              <button
                type="button"
                key={r.key}
                className={`bookingsReportRangeBtn${range === r.key ? " active" : ""}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="bookingsReportActions">
            <button type="button" onClick={handlePrint} disabled={!!busyAction}>
              {busyAction === "print" ? "Sending..." : "Print to Printer"}
            </button>
            <button type="button" onClick={handlePdf} disabled={!!busyAction}>
              {busyAction === "pdf" ? "Generating..." : "Save as PDF"}
            </button>
            <button type="button" onClick={handleEmail} disabled={!!busyAction}>
              {busyAction === "email" ? "Sending..." : "Send Email"}
            </button>
            <button type="button" onClick={handleLink} disabled={!!busyAction}>
              {busyAction === "link" ? "Creating..." : "Send a Link"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsReportMenu;
